import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LockIcon from "@material-ui/icons/Lock";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FormFieldRenderer from "./FormFieldRenderer";
import SupportContact from "./SupportContact";
import { validateAll } from "./utils/validate";
import { useOfferCheckoutStyles } from "./offerCheckout.styles";
import useOfferAnalyticsScripts from "./hooks/useOfferAnalyticsScripts";
import AiSensyLogo from "../../static/AiSensyLogo.svg";
import { countries } from "../PaymentPages/countryData";
import offerApi, { thankYouPath } from "../../services/offerApi";
import { isSalesRoute } from "../../services/salesRoute";
import useUtmCapture from "../../hooks/useUtmCapture";
import { CHECKOUT_PREFIX } from "../../config/config";
import { saveSignupPrefill } from "../../utils/signupPrefill";

/**
 * Lazy-loads the Razorpay checkout script once per page.
 * Resolves when window.Razorpay is available.
 */
const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.Razorpay) return resolve(window.Razorpay);

    const scriptId = "razorpay-checkout-script";
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay));
      existing.addEventListener("error", () =>
        reject(new Error("Razorpay script failed to load"))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Razorpay script failed to load"));
    document.body.appendChild(script);
  });

/** Round to 2 decimals (paise). Pure — module scope so it's referentially
 *  stable and safe to use inside hooks without a dependency entry. */
const round2 = (x) => Math.round(x * 100) / 100;

// Whole months per billing cycle — used to show each cycle's effective
// monthly cost on the switch tabs.
const MONTHS_IN_CYCLE = { monthly: 1, quarterly: 3, yearly: 12 };

/**
 * Derives the display amounts for one resolved offer (base merged with the
 * selected billing option). Pure and offer-scoped so a combo page can price
 * each product card independently while the single-product path keeps using
 * the exact same numbers it always has.
 *   planBase — the plan's own price (never changes with the QR kit)
 *   base     — payable amount (plan + kit) the Pay button charges
 *   gst/total— GST split and the grand total
 */
const computePricing = (offer, kitSelected) => {
  const gstPct = Number(offer.gstPercentage || 0);
  const hasKit = Boolean(offer.qrKitAddon);
  if (gstPct <= 0) {
    const legacyBase = offer.basePrice != null ? offer.basePrice : offer.price;
    return {
      planBase: legacyBase,
      base: legacyBase,
      gst: offer.gstAmount || 0,
      total: offer.price,
    };
  }
  const baseAmount = Number(
    offer.basePrice != null ? offer.basePrice : offer.price
  );
  const baseSum =
    baseAmount + (kitSelected && hasKit ? offer.qrKitAddon.basePrice : 0);
  const gst = round2((baseSum * gstPct) / 100);
  return {
    planBase: baseAmount,
    base: baseSum,
    gst,
    total: round2(baseSum + gst),
  };
};

// Same collapse threshold used per card and by the single-product path.
const BENEFITS_PREVIEW = 3;

/**
 * One value-stack pill. Shared by the single-product path and the combo cards
 * so the two can't drift. A pill with neither `originalAmount` nor `highlight`
 * (every page before BIL-409) renders exactly as before:
 *   originalAmount — list price shown struck through ahead of `amount`
 *   highlight      — accent styling that calls out a bonus inclusion
 */
const ValuePill = ({ pill, currency, fmt, classes }) => (
  <span
    className={
      pill.highlight ? `${classes.pill} ${classes.pillHighlight}` : classes.pill
    }
  >
    <span className={classes.pillCheck}>&#10003;</span>
    {pill.originalAmount != null ? (
      <span className={classes.pillStrike}>
        {currency}
        {fmt(pill.originalAmount)}
      </span>
    ) : null}
    {pill.amount != null ? (
      <>
        {currency}
        {fmt(pill.amount)}{" "}
      </>
    ) : null}
    {pill.label}
  </span>
);

const CheckoutFormPanel = ({ offer: baseOffer }) => {
  const classes = useOfferCheckoutStyles();
  // Sales-mirror routes (INT-56) inject NO per-page trackers — `{}` means
  // "inject nothing". The global GA/GTM/OpenAI tags are gated off in
  // index.html on the same path check, so `window.oaiq`/`window.fbq` never
  // exist here and the purchase-time conversion pings below no-op on their own.
  useOfferAnalyticsScripts(isSalesRoute() ? {} : baseOffer.tracking);
  const utmParams = useUtmCapture();

  // Microsoft Clarity — hardcoded org project, offer pages only. The shared
  // analytics hook carries no Clarity; the regional/webinar pages inject it
  // separately. Same script id as those, so it loads once and never doubles up.
  useEffect(() => {
    // Sales-mirror routes (INT-56) fire NO client-side tracking — Clarity does
    // session recording + heatmaps, so it is suppressed here like every other
    // pixel/analytics tag.
    if (isSalesRoute()) return;
    const clarityScriptId = "clarity-script";
    if (document.getElementById(clarityScriptId)) return;
    const clarityScript = document.createElement("script");
    clarityScript.id = clarityScriptId;
    clarityScript.type = "text/javascript";
    clarityScript.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "v6d5f4kx6j");
    `;
    document.head.appendChild(clarityScript);
    return () => {
      const el = document.getElementById(clarityScriptId);
      if (el) el.remove();
    };
  }, []);

  /**
   * Billing-cycle switch. A page that sells the same product monthly AND
   * yearly ships both as billingOptions; picking one spreads its fields
   * (price, mrp, pills, inclusions, and the plan sent to the gateway) over
   * the offer, so every read below stays a plain `offer.*` and cannot fall
   * out of sync with the selection.
   *
   * Single-plan pages — every existing offer — get one option and the
   * original object untouched, so nothing about them changes.
   */
  const billingOptions = useMemo(
    () => baseOffer.billingOptions || [],
    [baseOffer]
  );
  const showBillingSwitch = billingOptions.length > 1;
  // Keyed by plan id, not period, so two options can share a period (e.g. two
  // one-time packs). Cycle pages have distinct ids, so they're unaffected.
  const [billingPlanId, setBillingPlanId] = useState(
    ((billingOptions[0] || {})._plan || {}).id
  );

  /**
   * Combo pages (only the combined offer page today) tag each plan with a
   * `group`, and every distinct group becomes ONE selectable product card
   * with its own billing switch. The distinct groups are read in config
   * order.
   *
   * Zero or one distinct group === every existing page: `isMultiGroup` is
   * false, none of the grouping state or the card block below runs, and the
   * original single-card + switch path renders byte-for-byte as before.
   */
  const groups = useMemo(() => {
    const seen = [];
    billingOptions.forEach((o) => {
      if (o.group != null && !seen.includes(o.group)) seen.push(o.group);
    });
    return seen;
  }, [billingOptions]);
  const isMultiGroup = groups.length > 1;

  // Pre-select the first group that carries a `popular` plan (AI Agent in the
  // combo design); fall back to the last group when none is flagged.
  const [selectedGroup, setSelectedGroup] = useState(() => {
    const popularOpt = billingOptions.find((o) => o.popular && o.group != null);
    if (popularOpt) return popularOpt.group;
    return groups.length ? groups[groups.length - 1] : undefined;
  });
  // Each card remembers its own selected cycle, seeded to that group's first
  // billing option.
  const [groupPeriods, setGroupPeriods] = useState(() => {
    const map = {};
    billingOptions.forEach((o) => {
      if (o.group != null && map[o.group] == null) map[o.group] = o.period;
    });
    return map;
  });
  // Per-card "View all inclusions" state so expanding one card never expands
  // the other. (Single-product pages keep using `benefitsOpen` below.)
  const [openGroups, setOpenGroups] = useState({});

  const offer = useMemo(() => {
    // Combo page: the active offer is the SELECTED group's selected cycle, so
    // pricing, the sticky bar, and create-intent below all read from the card
    // the buyer actually picked — no change to any of those call sites.
    if (isMultiGroup) {
      const opts = billingOptions.filter((o) => o.group === selectedGroup);
      const selected =
        opts.find((o) => o.period === groupPeriods[selectedGroup]) ||
        opts[0] ||
        billingOptions[0];
      return { ...baseOffer, ...selected };
    }
    if (!showBillingSwitch) return baseOffer;
    const selected =
      billingOptions.find((o) => o._plan?.id === billingPlanId) ||
      billingOptions[0];
    return { ...baseOffer, ...selected };
  }, [
    baseOffer,
    billingOptions,
    billingPlanId,
    showBillingSwitch,
    isMultiGroup,
    selectedGroup,
    groupPeriods,
  ]);

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // "Request a Callback" runs the same submission as Pay Now minus the
  // gateway, so it needs its own pending flag — otherwise clicking it would
  // put the Pay Now button into a spinner too.
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);
  const [callbackSent, setCallbackSent] = useState(false);
  // Request-callback pages are lead-first: until the callback is requested,
  // the buyer sees ONLY the "Request Callback" button — no Pay Now and no full
  // feature list. Once the lead is saved + sent to LSQ (callbackSent), the
  // inclusions and Pay Now are revealed.
  const isCallbackGate = baseOffer.showCallbackRequest && !callbackSent;
  // After the callback is requested on a callback page: drop the lead form,
  // auto-expand the inclusions, and show Pay Now below the plan.
  const isCallbackDone = baseOffer.showCallbackRequest && callbackSent;
  // Benefits collapse to the first few so the price and the form stay above
  // the fold; the rest are one click away.
  const [benefitsOpen, setBenefitsOpen] = useState(false);
  const [kitSelected, setKitSelected] = useState(false); // QR kit OFF by default
  // Selected country for international phone fields. Seeded from the page's
  // `defaultCountry` so a non-IN page opens on the right dial code.
  const [country, setCountry] = useState(
    () =>
      countries.find((c) => c.code === (offer.defaultCountry || "IN")) ||
      countries.find((c) => c.code === "IN")
  );
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // Pre-warm the Razorpay script so the Pay Now click feels instant.
  useEffect(() => {
    loadRazorpayScript().catch((err) => {
      // Non-fatal: if this fails, handleSubmit will retry + surface the error.
      console.warn("[CheckoutFormPanel] Razorpay preload failed:", err);
    });
  }, []);

  // Cart-recovery return link: if the URL carries an encrypted `?prefill=`
  // blob, exchange it server-side for the buyer's saved fields and seed the
  // form so they can pay in one tap. Best-effort — a bad/absent token leaves
  // the form blank (normal flow).
  useEffect(() => {
    let token = null;
    try {
      token = new URLSearchParams(window.location.search).get("prefill");
    } catch {
      token = null;
    }
    if (!token) return undefined;
    let cancelled = false;
    offerApi
      .getPrefill(token)
      .then((f) => {
        if (cancelled || !f) return;
        // The prefill payload is keyed by customer COLUMN (fullName, email,
        // organizationName…), but the form's `values` are keyed by each field's
        // `key` (e.g. the name field has key "name", column "fullName"). Map
        // column→key via the offer's own formFields so every page's fields
        // populate correctly.
        const seed = {};
        (offer.formFields || []).forEach((field) => {
          const v = field.column ? f[field.column] : undefined;
          if (v !== undefined && v !== null && v !== "") seed[field.key] = v;
        });
        if (Object.keys(seed).length) {
          setValues((prev) => ({ ...prev, ...seed }));
        }
        if (f.isoCode) {
          const c = countries.find((x) => x.code === f.isoCode);
          if (c) setCountry(c);
        }
      })
      .catch(() => {
        /* prefill is best-effort; never block the page */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, v) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const showError = (msg) =>
    setToast({ open: true, msg, severity: "error" });

  /**
   * Same validation and the same endpoint as Pay Now — the server captures
   * the lead identically and simply stops before the payment gateway. The
   * buyer stays on the page; sales calls them back.
   */
  const handleCallbackRequest = async () => {
    const nextErrors = validateAll(offer.formFields, values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setCallbackSubmitting(true);
    try {
      await offerApi.createPaymentIntent(
        offer.slug,
        offer,
        values,
        { dialCode: country?.dial_code || "+91", isoCode: country?.code || "IN" },
        utmParams,
        kitSelected,
        true, // requestCallback
      );
      setCallbackSent(true);
      setBenefitsOpen(true); // auto-expand the full inclusions after callback
      setToast({
        open: true,
        severity: "success",
        msg: "Thanks! Our team will call you back shortly.",
      });
    } catch (err) {
      console.error("[CheckoutFormPanel] callback request failed:", err);
      if (err && err.errors && typeof err.errors === "object") {
        setErrors(err.errors);
      }
      showError(err?.message || "Could not submit your request. Please try again.");
    } finally {
      setCallbackSubmitting(false);
    }
  };

  const handleSubmit = async (ev) => {
    if (ev) ev.preventDefault();

    const nextErrors = validateAll(offer.formFields, values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const Razorpay = await loadRazorpayScript();

      // the thank-you page pre-fills its signup form from these
      const byColumn = {};
      (offer.formFields || []).forEach((field) => {
        if (field.column) byColumn[field.column] = values[field.key];
      });
      saveSignupPrefill({
        fullName: byColumn.fullName,
        email: byColumn.email,
        phone: byColumn.phone,
        countryCode: country?.dial_code || "+91",
        company: byColumn.organizationName,
      });

      // Server re-validates price from config — we never send the amount.
      const intent = await offerApi.createPaymentIntent(
        offer.slug,
        offer,
        values,
        { dialCode: country?.dial_code || "+91", isoCode: country?.code || "IN" },   
        utmParams,   // captured from the ad URL
        kitSelected
      );

      // XPay and Stripe are REDIRECT gateways: they return a forward URL and the
      // buyer pays on the gateway's hosted page, then returns to redirectUrl.
      // There is no in-page modal, so the checkout hands off here.
      if (intent.paymentGateway === "xpay" || intent.paymentGateway === "stripe") {
        if (!intent.fwdUrl) {
          throw new Error("Payment could not be started. Please try again.");
        }
        // BIL-449: the thank-you page falls back to this id if XPay's return
        // URL does not carry `xpay_intent_id` in a readable form.
        try {
          window.sessionStorage.setItem("xpay-intent-id", intent.intentId || "");
        } catch (e) {}
        window.location.href = intent.fwdUrl;
        return;
      }

      if (intent.paymentGateway !== "razorpay") {
        throw new Error(
          `Unsupported payment gateway: ${intent.paymentGateway}`
        );
      }

      const razorpayOptions = {
        key: intent.keyId,
        amount: Math.round(Number(intent.amount) * 100),
        currency: intent.currency,
        name: "AISENSY COMMUNICATIONS PVT LTD",
        description: `Payment for ${offer.productName}`,
        order_id: intent.intentId,
        prefill: {
          name: values.name,
          ...(values.email ? { email: values.email } : {}),
          contact: `+91${values.phone}`,
        },
        notes: intent.notes || {},
        theme: { color: "#04CF66" },
        retry: { enabled: true },
        remember_customer: true,
        modal: {
          backdropclose: false,
          escape: false,
          handleback: true,
          confirm_close: true,
          ondismiss: () => setSubmitting(false),
        },
        handler: function () {
          if (window.fbq && offer?.tracking?.purchaseEvent !== false) {
            window.fbq("track", "Purchase", {
              value: Number(intent.amount),
              currency: intent.currency || "INR",
            });
          }
          // OpenAI (ChatGPT) ads conversion — BIL-310. `amount` is an integer
          // in the currency's lowest denomination (paise), and event_id is the
          // gateway order id so it can dedupe against a server event later.
          if (window.oaiq) {
            window.oaiq(
              "measure",
              "order_created",
              {
                type: "contents",
                amount: Math.round(Number(intent.amount) * 100),
                currency: intent.currency || "INR",
                contents: [
                  {
                    id: offer.slug,
                    name: offer.productName,
                    content_type: "product",
                    quantity: 1,
                  },
                ],
              },
              { event_id: intent.intentId }
            );
          }
          // Razorpay captured the payment. The Razorpay webhook is the
          // authoritative writer of isPaymentCompleted.
          //
          // Digital-only pages have nothing to ship, so they go straight to
          // the plans thank-you page — same destination as the two-step plans
          // checkout, and it needs no customer _id to render.
          //
          // Physical offers redirect to the shipping page using the
          // customerId from create-intent. That page gates on
          // isPaymentCompleted — if the webhook is still in flight the user
          // sees a retry state, which reloads when the DB flips.
          window.location.href = offer.digitalOnly
            ? `${CHECKOUT_PREFIX}${thankYouPath(offer.slug, utmParams)}`
            : `${CHECKOUT_PREFIX}/offer/${offer.slug}/shipping/${intent.customerId}`;
        },
      };

      const rzp = new Razorpay(razorpayOptions);
      rzp.on("payment.failed", (response) => {
        const desc =
          response?.error?.description || "Payment failed";
        const reason = response?.error?.reason || "unknown";
        showError(`Payment failed: ${desc} (${reason})`);
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      console.error("[CheckoutFormPanel] handleSubmit error:", err);

      // Map backend field errors to inline state when available.
      if (err && err.errors && typeof err.errors === "object") {
        setErrors(err.errors);
      }
      showError(err?.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  // Whole rupees stay clean ("₹1,925"); anything with paise keeps both
  // decimals so a GST-inclusive total reads "₹2,271.50", not "₹2,271.5".
  const priceFmt = (n) => {
    const v = Number(n);
    return v.toLocaleString(
      "en-IN",
      Number.isInteger(v)
        ? {}
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    );
  };

  const gstPct = Number(offer.gstPercentage || 0);
  const hasKit = Boolean(offer.qrKitAddon);
  // Opt-in: plan card headline shows the GST-inclusive total (what the buyer
  // is actually charged). The sticky bar still shows base "+ GST".
  const gstInclusiveHeadline = Boolean(offer.showGstInclusivePrice) && gstPct > 0;
  // Mirror opt-in: keep the "base + GST" headline, but spell the charged
  // total out underneath. Never both — an inclusive headline already states
  // the total, so the sub-line would repeat it.
  const gstInclusiveSubNote =
    !gstInclusiveHeadline &&
    Boolean(offer.showGstInclusiveSubNote) &&
    gstPct > 0;
  // The sticky strip always leads with the GST-inclusive total: it is too
  // narrow for the card's base + sub-note + struck MRP layout.
  const stickyGstInclusive = gstInclusiveHeadline || gstInclusiveSubNote;
  // True only when this page actually charges GST (INR pages).
  const hasGst = gstPct > 0 || Number(offer.gstAmount || 0) > 0;
  // Extra class applied to both cards when the page opts into the flat look.
  const flatCls = offer.flatPageStyle ? classes.cardFlat : "";
  // Pages that declare a value stack get the pill row + "Total Value" label.
  const valuePills = offer.valuePills || [];
  const hasValueStack = valuePills.length > 0;
  // Only collapse when there is enough to be worth collapsing. Value-stack
  // pages always collapse; other pages opt in via `collapseBenefits` in the
  // region config (e.g. the full-Basic-list pages), so existing long-list
  // pages keep their current rendering.
  const canCollapseBenefits =
    (hasValueStack || offer.collapseBenefits) &&
    (offer.includes || []).length > BENEFITS_PREVIEW + 1;
  const visibleIncludes =
    canCollapseBenefits && !benefitsOpen
      ? offer.includes.slice(0, BENEFITS_PREVIEW)
      : offer.includes || [];

  // Same formula as each product card (computePricing), so the sticky bar and
  // Pay button always mirror whichever card/cycle is active.
  const pricing = useMemo(
    () => computePricing(offer, kitSelected),
    [offer, kitSelected]
  );

  /**
   * One selectable product card for a combo-page group. Renders the same card
   * anatomy as the single-product path (name, per-group billing switch, price,
   * pills, inclusions) plus a radio + selected/dimmed styling. Only reached
   * when isMultiGroup is true, so it can never affect existing pages.
   */
  const renderGroupCard = (group) => {
    const groupOptions = billingOptions.filter((o) => o.group === group);
    const selectedOpt =
      groupOptions.find((o) => o.period === groupPeriods[group]) ||
      groupOptions[0] ||
      {};
    const cardOffer = { ...baseOffer, ...selectedOpt };
    const isSelected = group === selectedGroup;

    const cardGstPct = Number(cardOffer.gstPercentage || 0);
    const cardPricing = computePricing(cardOffer, kitSelected);
    const cardGstInclusive =
      Boolean(cardOffer.showGstInclusivePrice) && cardGstPct > 0;
    const cardGstInclusiveSubNote =
      !cardGstInclusive &&
      Boolean(cardOffer.showGstInclusiveSubNote) &&
      cardGstPct > 0;
    const cardValuePills = cardOffer.valuePills || [];
    const cardHasValueStack = cardValuePills.length > 0;
    const cardCanCollapse =
      (cardHasValueStack || cardOffer.collapseBenefits) &&
      (cardOffer.includes || []).length > BENEFITS_PREVIEW + 1;
    const cardOpen = Boolean(openGroups[group]);
    const cardVisibleIncludes =
      cardCanCollapse && !cardOpen
        ? cardOffer.includes.slice(0, BENEFITS_PREVIEW)
        : cardOffer.includes || [];
    const showGroupSwitch = groupOptions.length > 1;

    return (
      <div
        key={group}
        role="radio"
        aria-checked={isSelected}
        tabIndex={0}
        onClick={() => setSelectedGroup(group)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setSelectedGroup(group);
          }
        }}
        className={`${classes.planCard} ${classes.planCardSelectable} ${
          isSelected ? classes.planCardSelected : classes.planCardDimmed
        } ${flatCls}`}
      >
        {selectedOpt.popular && cardOffer.discountLabel ? (
          <span className={classes.planCardBadge}>
            {cardOffer.discountLabel}
          </span>
        ) : null}
        <span
          className={`${classes.planCardRadio} ${
            isSelected ? classes.planCardRadioSelected : ""
          }`}
          aria-hidden="true"
        />

        <div className={classes.planHeader}>
          <div style={{ paddingRight: 28 }}>
            <div className={classes.productName}>{cardOffer.productName}</div>
            <div className={classes.tagline}>{cardOffer.tagline}</div>
          </div>
        </div>

        {showGroupSwitch ? (
          <div
            className={classes.billingRadioGroup}
            role="radiogroup"
            aria-label="Billing duration"
          >
            {groupOptions.map((opt) => {
              const months = MONTHS_IN_CYCLE[opt.period];
              const perMonth =
                months && opt.price ? Math.round(opt.price / months) : null;
              const active = opt.period === selectedOpt.period;
              return (
                <button
                  key={opt.period}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={(e) => {
                    // Picking a cycle also selects the card it belongs to, and
                    // must not bubble to the card's own select handler.
                    e.stopPropagation();
                    setSelectedGroup(group);
                    setGroupPeriods((p) => ({ ...p, [group]: opt.period }));
                  }}
                  className={`${classes.billingRadioOption} ${
                    active ? classes.billingRadioOptionActive : ""
                  }`}
                >
                  <span
                    className={`${classes.billingRadioDot} ${
                      active ? classes.billingRadioDotActive : ""
                    }`}
                    aria-hidden="true"
                  />
                  <span className={classes.billingRadioText}>
                    <span className={classes.billingSwitchLabel}>
                      {opt.label}
                    </span>
                    {perMonth ? (
                      <span className={classes.billingSwitchPerMonth}>
                        {cardOffer.currency}
                        {priceFmt(perMonth)}/mo
                        {cardGstPct > 0 ? " + GST" : ""}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}

        <div className={classes.priceRow}>
          <span className={classes.price}>
            {cardOffer.currency}
            {priceFmt(
              cardGstInclusive ? cardPricing.total : cardPricing.planBase
            )}
          </span>
          {cardGstInclusive ? (
            <span className={classes.gstNote}>incl. GST</span>
          ) : cardGstPct > 0 ? (
            <span className={classes.gstNote}>+ GST</span>
          ) : cardPricing.gst > 0 ? (
            <span className={classes.gstNote}>
              + {cardOffer.currency}
              {priceFmt(cardPricing.gst)} GST
            </span>
          ) : null}
          {cardOffer.mrp && cardOffer.mrp > cardPricing.total ? (
            <span
              className={cardHasValueStack ? classes.totalValue : classes.mrp}
            >
              {cardOffer.currency}
              {priceFmt(cardOffer.mrp)}
              {cardGstInclusive ? " + GST" : ""}
            </span>
          ) : null}
          {cardOffer.billing ? (
            <span className={classes.cycle}>{cardOffer.billing}</span>
          ) : null}
          {cardGstInclusive ? (
            <div className={classes.priceSubNote}>
              ({cardOffer.currency}
              {priceFmt(cardPricing.base)} + GST)
            </div>
          ) : cardGstInclusiveSubNote ? (
            // Headline is the GST-exclusive base, so name the amount the
            // buyer is actually charged
            <div className={classes.priceSubNote}>
              ({cardOffer.currency}
              {priceFmt(cardPricing.total)} incl. GST)
            </div>
          ) : null}
        </div>

        {cardHasValueStack ? (
          <div className={classes.pillRow}>
            {cardValuePills.map((pill, i) => (
              <ValuePill
                key={i}
                pill={pill}
                currency={cardOffer.currency}
                fmt={priceFmt}
                classes={classes}
              />
            ))}
          </div>
        ) : null}

        {cardOffer.freeBonusLabel ? (
          <div className={classes.bonus}>
            <CheckCircleIcon style={{ fontSize: 18, color: "#236E34" }} />
            {cardOffer.freeBonusLabel} included
          </div>
        ) : null}

        {cardOffer.includes && cardOffer.includes.length > 0 ? (
          <>
            <ul className={classes.includesList}>
              {cardVisibleIncludes.map((item, i) => (
                <li key={i} className={classes.includeItem}>
                  <CheckCircleIcon className={classes.checkIcon} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {cardCanCollapse ? (
              <button
                type="button"
                className={classes.benefitsToggle}
                aria-expanded={cardOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenGroups((p) => ({ ...p, [group]: !p[group] }));
                }}
              >
                {cardOpen ? "Show less" : "View all inclusions"}
                <ExpandMoreIcon
                  style={{
                    fontSize: 18,
                    transition: "transform 150ms ease",
                    transform: cardOpen ? "rotate(180deg)" : "none",
                  }}
                />
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  // Combo pages render one selectable card per group; every existing page
  // renders the single planCard below, unchanged.
  const planSection = isMultiGroup ? (
    <>
      <div className={classes.groupIntro}>
        Choose your plan — select the offer and duration you want.
      </div>
      {groups.map((group) => renderGroupCard(group))}
    </>
  ) : (
    <>
      {/* plan summary */}
        <div className={`${classes.planCard} ${flatCls}`}>
          <div className={classes.planHeader}>
            <div>
              <div className={classes.productName}>{offer.productName}</div>
              <div className={classes.tagline}>{offer.tagline}</div>
            </div>
            {offer.discountLabel ? (
              <span className={classes.discountBadge}>
                {offer.discountLabel}
              </span>
            ) : null}
          </div>

          {showBillingSwitch ? (
            <div
              className={classes.billingRadioGroup}
              role="radiogroup"
              aria-label="Billing duration"
            >
              {billingOptions.map((opt) => {
                // Effective monthly cost of each cycle, so a longer commitment
                // visibly reads as cheaper per month (₹1,425 → ₹1,333 → ₹1,200).
                // Base is GST-exclusive; whole months per cycle.
                const months = MONTHS_IN_CYCLE[opt.period];
                const perMonth =
                  months && opt.price
                    ? Math.round(opt.price / months)
                    : null;
                const active = opt._plan?.id === offer._plan?.id;
                return (
                  <button
                    key={opt._plan?.id || opt.period}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setBillingPlanId(opt._plan?.id)}
                    className={`${classes.billingRadioOption} ${
                      active ? classes.billingRadioOptionActive : ""
                    }`}
                  >
                    <span
                      className={`${classes.billingRadioDot} ${
                        active ? classes.billingRadioDotActive : ""
                      }`}
                      aria-hidden="true"
                    />
                    <span className={classes.billingRadioText}>
                      <span className={classes.billingSwitchLabel}>
                        {opt.switchLabel || opt.label}
                      </span>
                      {opt.switchSubLabel ? (
                        <span className={classes.billingSwitchPerMonth}>
                          {opt.switchSubLabel}
                        </span>
                      ) : perMonth ? (
                        <span className={classes.billingSwitchPerMonth}>
                          {offer.currency}
                          {priceFmt(perMonth)}/mo
                          {gstPct > 0 ? " + GST" : ""}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className={classes.priceRow}>
            <span className={classes.price}>
              {offer.currency}
              {priceFmt(
                gstInclusiveHeadline ? pricing.total : pricing.planBase
              )}
            </span>
            {gstInclusiveHeadline ? (
              // Headline is the amount actually charged, GST already in it.
              <span className={classes.gstNote}>incl. GST</span>
            ) : gstPct > 0 ? (
              // v2: GST charged on top — show "+ GST" without the amount.
              <span className={classes.gstNote}>+ GST</span>
            ) : pricing.gst > 0 ? (
              // Legacy: keep the explicit GST amount (unchanged behavior).
              <span className={classes.gstNote}>
                + {offer.currency}
                {priceFmt(pricing.gst)} GST
              </span>
            ) : null}
            {/* {offer.mrp && offer.mrp > pricing.total ? ( */}
              <span
                className={
                  hasValueStack ? classes.totalValue : classes.mrp
                }
              >
                {offer.currency}
                {priceFmt(offer.mrp)}
                {/* MRP is a GST-exclusive figure, so label it when the
                    headline next to it is GST-inclusive. */}
                {gstInclusiveHeadline ? " + GST" : ""}
              </span>
            {/* ) : null} */}
            {offer.billing ? (
              <span className={classes.cycle}>{offer.billing}</span>
            ) : null}
            {/* Spells out what the GST-inclusive headline is made of. */}
            {gstInclusiveHeadline ? (
              <div className={classes.priceSubNote}>
                ({offer.currency}
                {priceFmt(pricing.base)} + GST)
              </div>
            ) : gstInclusiveSubNote ? (
              // Headline is the GST-exclusive base, so name the amount the
              // buyer is actually charged.
              <div className={classes.priceSubNote}>
                ({offer.currency}
                {priceFmt(pricing.total)} incl. GST)
              </div>
            ) : null}
          </div>


          {hasValueStack ? (
            <div className={classes.pillRow}>
              {valuePills.map((pill, i) => (
                <ValuePill
                  key={i}
                  pill={pill}
                  currency={offer.currency}
                  fmt={priceFmt}
                  classes={classes}
                />
              ))}
            </div>
          ) : null}

          {offer.freeBonusLabel ? (
            <div className={classes.bonus}>
              <CheckCircleIcon style={{ fontSize: 18, color: "#236E34" }} />
              {offer.freeBonusLabel} included
            </div>
          ) : null}

          {offer.includes && offer.includes.length > 0 ? (
            <>
              <ul className={classes.includesList}>
                {visibleIncludes.map((item, i) => (
                  <li key={i} className={classes.includeItem}>
                    <CheckCircleIcon className={classes.checkIcon} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {canCollapseBenefits ? (
                <button
                  type="button"
                  className={classes.benefitsToggle}
                  aria-expanded={benefitsOpen}
                  onClick={() => setBenefitsOpen((v) => !v)}
                >
                  {benefitsOpen ? "Show less" : "View all inclusions"}
                  <ExpandMoreIcon
                    style={{
                      fontSize: 18,
                      transition: "transform 150ms ease",
                      transform: benefitsOpen ? "rotate(180deg)" : "none",
                    }}
                  />
                </button>
              ) : null}
            </>
          ) : null}
        </div>
    </>
  );

  const body = (
    <>
        {/* Plan card sits above the lead form: show the plan and duration
            first, then capture the buyer's details below. */}
        {planSection}

        {isCallbackDone ? (
          // Lead already captured via Request Callback — no form; Pay Now sits
          // directly under the (auto-expanded) plan. Mobile uses the sticky bar.
          <div className={`${classes.ctaRow} ${classes.hideOnMobile}`}>
            <Button
              variant="contained"
              className={classes.submitButton}
              disabled={submitting}
              onClick={handleSubmit}
              startIcon={
                !submitting ? <LockIcon style={{ fontSize: 18 }} /> : undefined
              }
            >
              {submitting ? (
                <CircularProgress size={22} style={{ color: "#ffffff" }} />
              ) : (
                offer.ctaText || "Buy Now"
              )}
            </Button>
          </div>
        ) : (
        <form
          className={`${classes.formCard} ${flatCls}`}
          onSubmit={handleSubmit}
          noValidate
        >
          <div className={classes.formTitle}>Enter your details</div>

          {(offer.formFields || []).map((field) => (
            <FormFieldRenderer
              key={field.key}
              field={field}
              value={values[field.key]}
              error={errors[field.key]}
              onChange={setField}
              country={country}
              onCountryChange={(c) => c && setCountry(c)}
            />
          ))}

          {hasKit ? (
            <label className={classes.kitOption}>
              <input
                type="checkbox"
                checked={kitSelected}
                onChange={(e) => setKitSelected(e.target.checked)}
                className={classes.kitCheckbox}
              />
              <span className={classes.kitLabel}>
                +{offer.currency}
                {priceFmt(offer.qrKitAddon.basePrice)} Add QR Stand Kit
              </span>
            </label>
          ) : null}

          <div className={`${classes.ctaRow} ${classes.hideOnMobile}`}>
          {offer.showCallbackRequest && !callbackSent ? (
            <Button
              variant="contained"
              className={classes.submitButton}
              disabled={submitting || callbackSubmitting}
              onClick={handleCallbackRequest}
            >
              {callbackSubmitting ? (
                <CircularProgress size={22} style={{ color: "#ffffff" }} />
              ) : (
                "Request Callback"
              )}
            </Button>
          ) : null}
          {!isCallbackGate ? (
            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
              disabled={submitting}
              startIcon={
                !submitting ? <LockIcon style={{ fontSize: 18 }} /> : undefined
              }
            >
              {submitting ? (
                <CircularProgress size={22} style={{ color: "#ffffff" }} />
              ) : (
                <>{offer.ctaText || "Buy Now"}</>
              )}
            </Button>
          ) : null}
          </div>
        </form>
        )}

        {offer.hideSupportContact ? null : <SupportContact />}
        {/* Keeps the fixed bar from covering the last element on mobile. */}
        <div className={classes.stickySpacer} />
    </>
  );

  return (
    <div
      className={`${classes.pageRoot} ${
        offer.flatPageStyle ? classes.pageRootFlat : ""
      }`}
    >
      <div className={classes.pageHeader}>
        <img src={AiSensyLogo} alt="AiSensy" className={classes.pageLogo} />
      </div>
      <div className={classes.pageContainer}>{body}</div>

      {/* Mobile-only sticky pay bar. The price mirrors the in-form CTA
          exactly — including the QR-kit add-on, so ticking the kit updates
          both. Submits through the same handleSubmit, so validation and the
          Razorpay handoff are identical on both breakpoints. */}
      <div className={classes.stickyBar}>
        {/* Callback-enabled pages give the bar to the two actions and move the
            amount onto the Pay button, so the strip isn't price + two CTAs at
            375px. Every other page keeps the price block it has today. */}
        {isCallbackGate ? null : (
        <div className={classes.stickyPriceWrap}>
          <div className={classes.stickyPriceRow}>
            {/* Mirrors the plan card: headline is the amount actually
                charged, with the base + GST spelled out underneath. */}
            <span className={classes.stickyPrice}>
              {offer.currency}
              {priceFmt(
                stickyGstInclusive || gstPct === 0
                  ? pricing.total
                  : pricing.base
              )}
            </span>
            {/* When the sub-line below already spells out "base + GST",
                repeating "incl. GST" here says the same thing twice in a
                70px strip. One statement per fact. */}
            {/* Only label GST where GST actually applies. A non-INR page has
                none, so claiming "incl. GST" there would be false. */}
            {stickyGstInclusive || !hasGst ? null : (
              <span className={classes.stickyGstNote}>+ GST</span>
            )}
            {/* The struck MRP is omitted here when the headline is
                GST-inclusive: at 375px the extra "₹3,050 + GST" wraps to two
                lines and crowds the CTA. The plan card above already shows
                it. */}
            {/* Breakdown sits inline after the amount, so the whole price
                reads as one line with the CTA opposite it. */}
            {stickyGstInclusive ? (
              <span className={classes.stickySubNote}>
                ({offer.currency}
                {priceFmt(pricing.base)} + GST)
              </span>
            ) : null}
            {!stickyGstInclusive && offer.mrp && offer.mrp > pricing.total ? (
              <span className={classes.stickyMrp}>
                {offer.currency}
                {priceFmt(offer.mrp)}
              </span>
            ) : null}
          </div>
        </div>
        )}
        {offer.showCallbackRequest && !callbackSent ? (
          <Button
            variant="contained"
            className={`${classes.stickyButton} ${classes.stickyButtonWide}`}
            disabled={submitting || callbackSubmitting}
            onClick={handleCallbackRequest}
          >
            {callbackSubmitting ? (
              <CircularProgress size={20} style={{ color: "#ffffff" }} />
            ) : (
              "Request Callback"
            )}
          </Button>
        ) : null}
        {!isCallbackGate ? (
          <Button
            variant="contained"
            className={classes.stickyButton}
            disabled={submitting}
            onClick={handleSubmit}
            startIcon={
              !submitting ? <LockIcon style={{ fontSize: 16 }} /> : undefined
            }
          >
            {submitting ? (
              <CircularProgress size={20} style={{ color: "#ffffff" }} />
            ) : (
              offer.ctaText || "Buy Now"
            )}
          </Button>
        ) : null}
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CheckoutFormPanel;
